'use client'

import * as React from 'react'
import { ResizableContainer } from '~/sotano-kit/primitives/resizable-container'
import { ChangesTree } from '../../changes/components/sidebar/changes-tree'
import { useDiff } from '~/lib/diff/use-diff'
import { RootBlock } from '@basehub/data/replicache/index'
import { BlockDiff } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/components/diff/block-diff'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useCommitBlockTreeManager } from '~/hooks-data/blocks-tree'
import { parseTab, useTabsStore } from '../../explore/components/tabs/store'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { run } from '@basehub/utils'
import { BreadcrumbStickyContainer } from '../../explore/components/tabs/dashboard-breadcrumb'
import { Breadcrumb, BreadcrumbItem } from '~/sotano-kit/primitives/breadcrumb'
import { useBranchQuery } from '~/lib/branches/use-branch'
import { EditorModeProvider } from '../../explore/components/editor-mode-provider'
import { useUserData } from '~/hooks-data/use-user-data'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { tabContentWrapperId } from '../wrapper'
import { CheckIcon } from '@radix-ui/react-icons'
import { useWorkingDiff } from '~/lib/diff/use-working-diff'
import { useMutation } from '@tanstack/react-query'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { ApplySuggestionInput } from '@basehub/data/replicache/mutators/repo-space/apply-suggestion/mutators'
import { useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { clsx } from 'clsx'

export const SuggestionContent = ({ branchId }: { branchId: string }) => {
  const { data: branch } = useBranchQuery({ id: branchId })
  const { data: user } = useUserData(branch?.authorId)
  const treeContainerRef = React.useRef<HTMLDivElement>(null)
  const { treeNew: workingTree } = useWorkingDiff()
  const { data: newCommitBlockTree } = useCommitBlockTreeManager({
    commitId: branchId + 'working',
    rootBlockId: branch?.workingRootBlockId ?? undefined,
  })

  /**
   * Wondering if:
   * 1. we should compare commit against base branch head commit (we get a good diff, but we don't show the working changes (if they override))
   * 2. we should compare working tree against base working tree (we get good diff, but we kind of need to render conflicts, and we lose the commit history in the suggestion branch)
   *
   * Also, what the hell do we do if a user commits in the base branch?
   * In the case of 1, we'll get a bad diff. we'd need to introduce a common ancestor and create a "merge" diff.
   * In the case of 2, we'd get a good diff...
   *
   * So #2 sounds like the best option here. Without conflicts, at least for now.
   */

  const diffResult = useDiff({
    diffType: 'commit',
    treeOld: workingTree,
    treeNew: newCommitBlockTree as any,
    treeCommonAncestor: false,
    // treeCommonAncestor: headCommitTree,
  })

  const activeTab = useTabsStore((s) => s.activeTab)
  const selectBlockChangeForSuggestionTab = useTabsStore(
    (s) => s.selectBlockChangeForSuggestionTab
  )

  const parsedActiveTab = activeTab ? parseTab(activeTab) : null
  const _activeBlockId =
    parsedActiveTab?.type === 'suggestion'
      ? (parsedActiveTab.blockChangeId ?? null)
      : null
  const [defaultActiveBlockId, setDefaultActiveBlockId] = React.useState<
    string | null
  >(null)
  const resolveActiveBlockId = _activeBlockId ?? defaultActiveBlockId

  React.useEffect(() => {
    if (_activeBlockId) return
    const treeContainer = treeContainerRef.current
    if (!treeContainer) return

    const activeKey = treeContainer.querySelector<HTMLElement>(
      '[data-tree-list-child-active="true"]'
    )?.dataset.treeListChild
    if (activeKey && typeof activeKey === 'string') {
      setDefaultActiveBlockId(activeKey)
    }
  }, [_activeBlockId, diffResult.diff?.changesTree])

  const timeDiff = useTimeDiff(
    branch?.createdAt ? new Date(branch.createdAt) : new Date()
  )

  const { mutate: apply } = useApplySuggestion({ branchId })

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey && e.ctrlKey && e.key === 'Enter') {
        e.preventDefault()
        apply()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [apply])

  return (
    <EditorModeProvider value="read">
      <BreadcrumbStickyContainer>
        <Breadcrumb>
          <BreadcrumbItem active>
            Suggested by {user?.username}
            {branch?.createdAt ? ` ${timeDiff}` : ''}
          </BreadcrumbItem>
        </Breadcrumb>
        <div className="flex items-center gap-x-2">
          <button
            className={clsx(
              'flex items-center text-xs font-medium gap-1 hover:underline',
              branch?.inlineSuggestionAppliedAt
                ? 'text-green-diff'
                : 'text-dark-control-base'
            )}
            onClick={() => apply()}
          >
            <CheckIcon className="shrink-0" />
            <span>
              {branch?.inlineSuggestionAppliedAt
                ? 'Applied'
                : 'Apply suggestion (⌘⌃Enter)'}{' '}
            </span>
          </button>
        </div>
      </BreadcrumbStickyContainer>
      <div className="flex w-full flex-1 bg-dark-base">
        <ResizableContainer
          minWidth={150}
          maxWidth={250}
          defaultWidth={250}
          direction="right"
          className="flex flex-col w-full max-h-[var(--editor-height)] overflow-auto border-r border-dark-border-solid"
          widthLocalStorageKey="repo-commits-content-sidebar-width"
          noBorderHighlight
        >
          <ScrollArea viewportClassName="py-3" ref={treeContainerRef}>
            {run(() => {
              if (!diffResult.diff) {
                return (
                  <div className="space-y-1 px-3">
                    <Skeleton height={24} className="w-full" />
                    <Skeleton height={24} className="w-full" />
                    <Skeleton height={24} className="w-full" />
                    <Skeleton height={24} className="w-full" />
                    <Skeleton height={24} className="w-full" />
                  </div>
                )
              }

              if (diffResult.diff.changesCount < 1) return null

              return (
                <ChangesTree
                  className="flex-grow"
                  changesTreeManager={diffResult.diff.changesTree}
                  treeNew={diffResult.treeNew}
                  treeOld={diffResult.treeOld || null}
                  activeKey={
                    _activeBlockId ? { label: '', id: _activeBlockId } : null
                  }
                  autoFocusKey={resolveActiveBlockId}
                  onActiveKeyChange={(key) =>
                    selectBlockChangeForSuggestionTab({ blockChangeId: key })
                  }
                  selectedKeys={new Set()}
                  setSelectedKeys={() => {}}
                  changesCount={diffResult.diff.changesCount}
                  defaultSelectFirst
                />
              )
            })}
          </ScrollArea>
        </ResizableContainer>
        <ScrollArea
          orientation="vertical"
          id={tabContentWrapperId}
          className="max-h-[var(--editor-height)] flex-grow [&>div>div]:h-full"
        >
          {resolveActiveBlockId && parsedActiveTab?.type === 'suggestion' && (
            <div className="flex flex-col flex-1 w-full px-16 pt-14 relative scale-100 min-h-full">
              <BlockDiff
                blockId={resolveActiveBlockId}
                diffResult={diffResult}
                displayBlockLinks={false}
                diffTabPrefix={`suggestion:${branchId}:`}
              />

              <div className="h-32" />
            </div>
          )}
        </ScrollArea>
      </div>
    </EditorModeProvider>
  )
}

/** This inner hook can throw if repoUndoManager provider is not available */
export const usePrivateApplySuggestion = (
  params: undefined | { branchId: string }
) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const { data: branch } = useBranchQuery({ id: params?.branchId })
  const { treeNew: workingTree } = useWorkingDiff()
  const router = useRouter()
  const [backAfterApply, setBackAfterApply] = React.useState<string | null>(
    null
  )

  const input: ApplySuggestionInput | undefined =
    workingTree && branch && branch.workingRootBlockId
      ? {
          rootBlockId: branch.workingRootBlockId,
          baseBranchId: (workingTree.root.value as RootBlock).branchId,
          branchId: branch.id,
          now: new Date().toISOString(),
          baseRootBlockId: workingTree.root.value.id,
        }
      : undefined

  React.useEffect(() => {
    const url = new URL(window.location.href)
    const backAfterApply = url.searchParams.get('back-after-apply')
    if (backAfterApply) {
      setBackAfterApply(decodeURIComponent(backAfterApply))
    }
  }, [])

  return useMutation({
    mutationFn: async () => {
      if (!input) throw new Error('params are required')
      if (!replicache) return

      // advantages of keeping it client side is that we can add it to undo stack
      const { undo } = await replicache.mutate.applySuggestion(input)

      undoManager.current?.push({
        undo: [{ op: 'blockBatchMutations', args: { mutations: undo } }],
        redo: [{ op: 'applySuggestion', args: input }],
      })
    },
    onSuccess: () => {
      toast.success('Suggestion applied')
      if (backAfterApply) {
        router.push(backAfterApply)
      }
    },
  })
}

export const useApplySuggestion = (
  params: undefined | { branchId: string }
) => {
  try {
    return usePrivateApplySuggestion(params)
  } catch (error) {
    console.error('Error applying suggestion', error)
    return {
      mutate: () => {
        console.error('Error applying suggestion', error)
      },
    }
  }
}
